/**
 * Data layer for `publisher-specific-payout-crud-permission-scope`. Thin typed fetch wrappers over /api/v1/publisher-specific-payout-crud-permission-scope.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PublisherSpecificPayoutCrudPermissionScopeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPublisherSpecificPayoutCrudPermissionScopeResponse {
	count: number;
	items: PublisherSpecificPayoutCrudPermissionScopeRow[];
}

export interface CreatePublisherSpecificPayoutCrudPermissionScopeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/publisher-specific-payout-crud-permission-scope";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPublisherSpecificPayoutCrudPermissionScopeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPublisherSpecificPayoutCrudPermissionScopeResponse>(res);
}

export async function create(body: CreatePublisherSpecificPayoutCrudPermissionScopeBody): Promise<PublisherSpecificPayoutCrudPermissionScopeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PublisherSpecificPayoutCrudPermissionScopeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const publisherSpecificPayoutCrudPermissionScopeQueryKeys = {
	all: ["publisher-specific-payout-crud-permission-scope"] as const,
	list: () => [...publisherSpecificPayoutCrudPermissionScopeQueryKeys.all] as const,
	detail: (id: string) => [...publisherSpecificPayoutCrudPermissionScopeQueryKeys.all, id] as const,
};
